import moment from 'moment';
import { decodeHtmlEntitiesRegex, prepareEmailBodyHtml } from '@/components/search/email/emailBodyHtml';
import { needsEmailApi, NOTE_BODY_HTML_MAX, NOTE_BODY_PLAIN_MAX } from '@/components/search/email/emailConstants';
import { NotesAPIService } from '@/servicehandlers/NotesAPIService';

const notesAPIService = new NotesAPIService();

const TRASH_AFTER_CONNECT_STORAGE_KEY = 'nu-email-trash-after-connect';

/** Persisted preference: trash after a fully successful connect (default on). */
export function readTrashAfterConnectPref() {
  if (typeof window === 'undefined' || !window.localStorage) return true;
  const raw = window.localStorage.getItem(TRASH_AFTER_CONNECT_STORAGE_KEY);
  if (raw === null) return true;
  return raw === '1';
}

export function writeTrashAfterConnectPref(enabled) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.setItem(TRASH_AFTER_CONNECT_STORAGE_KEY, enabled ? '1' : '0');
}

/** Note created with no attachment/link errors — safe to auto-trash when trash-after-connect is on (default). */
export function isFullConnectSuccess(result) {
  return !!(result && result.noteCreated && !(result.errors && result.errors.length));
}

export function messageIdsForFullConnectSuccess(results) { // 6 May 2026 JCH: full success only — safe for auto-trash
  return (Array.isArray(results) ? results : [])
    .filter(isFullConnectSuccess)
    .map((r) => r.messageId)
    .filter((id) => id !== undefined && id !== null);
}

export async function trashMessagesAfterConnect(client, mailboxId, messageIds) {
  const ids = Array.isArray(messageIds) ? messageIds : [];
  if (!ids.length || !client || typeof client.softDeleteMessages !== 'function') {
    return { trashed: 0, failed: ids.length };
  }
  try {
    const { moved } = await client.softDeleteMessages(mailboxId, ids);
    const trashed = Array.isArray(moved) ? moved.length : ids.length;
    return { trashed, failed: Math.max(0, ids.length - trashed) };
  } catch (e) {
    return { trashed: 0, failed: ids.length };
  }
}

export function appendTrashAfterConnectSnack(base, { trashed = 0, failed = 0, trashFailed = 0 } = {}) {
  const snack = base && typeof base === 'object' ? { ...base } : { msg: String(base || '') };
  const failCount = trashFailed || failed;
  if (trashed > 0) {
    // Mixed bulk snacks already bake trash into the lead ("fully connected & trashed").
    if (snack.mixed || /fully connected & trashed/i.test(snack.msg || '')) {
      return snack;
    }
    const trashBit = trashed === 1 ? ' Moved to trash.' : ` Moved ${trashed} to trash.`;
    snack.msg = `${snack.msg || ''}${trashBit}`;
    return snack;
  }
  if (failCount > 0) {
    snack.color = snack.color || 'warning';
    snack.msg = `${snack.msg || ''} Could not move to trash.`;
  }
  return snack;
}

/**
 * Past-tense lead when leaving this email after a full connect (Older/Newer/List/end).
 * Stay keeps the present-tense import snack from connectRowSnackMessage.
 */
export function connectSnackForAdvance(msg) {
  const s = String(msg || '').trim();
  const m = s.match(
    /^Imported email as note on firm (.+?)\.( Moved (?:the successful connect to trash|to trash)\.)?$/,
  );
  if (m) {
    const firm = m[1];
    return m[2]
      ? `Previous email imported as a note on firm ${firm} and moved to trash.`
      : `Previous email imported as a note on firm ${firm}.`;
  }
  if (s && !/^Previous email/i.test(s)) {
    return `Previous email: ${s.replace(/\.$/, '')}.`;
  }
  return s;
}

/** Plain-text note import — same regex as email-in display. A textarea decoder parses the note as HTML. */
export function decodeHtmlEntities(text) {
  return decodeHtmlEntitiesRegex(text);
}

// 28 Aug 2026 JCH: NC-1644 — <head> dropped whole (a <title>'s text would otherwise survive the
// generic tag strip and leak into note bodies). Used by convert, strip and plain-text helpers below.
function removeNonVisibleHtmlBlocks(html) {
  return String(html)
    .replace(/<head\b[^>]*>[\s\S]*?<\/head>/gi, ' ')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<noscript\b[^>]*>[\s\S]*?<\/noscript>/gi, ' ')
    .replace(/<!--[\s\S]*?-->/g, ' ');
}

/** 18 Sep 2026 JCH: NC-1641 (NC-1644) — Convert To HTML. Only a full email/web *document* keeps its markup (the same
 * test Copy Note uses); every other body is escaped first, so <addresses> — and text like "plan <A>" — survive instead
 * of CKEditor treating the brackets as tags and eating them. A tag-name sniff was fooled by a lone <A>/<addr@x.com>. */
export function convertNoteBodyToHtml(original) {
  const safe = original != null ? original : ''; // a null body must not become the word "null"
  const cleaned = removeNonVisibleHtmlBlocks(safe);
  const base = noteBodyIsHtmlDocument(safe) ? cleaned : escapeHtmlText(cleaned);
  return base.replace(/(?:\r\n|\r|\n)/g, '<br>').replace(/�/g, "'");
}

export function stripHtml(html) {
  if (!html) return '';
  const cleaned = removeNonVisibleHtmlBlocks(html);
  if (typeof document === 'undefined') {
    return cleaned.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const d = document.createElement('div');
  d.innerHTML = cleaned;
  d.querySelectorAll('style, script, noscript, head').forEach((el) => el.remove());
  return (d.textContent || d.innerText || '').replace(/\s+/g, ' ').trim();
}

function emailNoteBodyText(full) {
  const plain = full.body != null ? String(full.body).trim() : '';
  if (plain) return decodeHtmlEntities(plain);
  const fromHtml = stripHtml(full.body_html);
  if (fromHtml) return decodeHtmlEntities(fromHtml);
  const snippet = full.snippet != null ? String(full.snippet).trim() : '';
  return snippet ? decodeHtmlEntities(snippet) : '';
}

export function formatReceived(iso) {
  if (!iso) return '';
  return moment(iso).format('DD MMM YYYY hh:mm a');
}

export function buildNoteBody(mailboxLabel, full) {
  const cc = full.cc != null ? String(full.cc).trim() : ''; // 24 Aug 2026 JCH: NC-1641 — show CC when Gmail provides one
  const lines = [
    `Mailbox: ${mailboxLabel}`,
    `From: ${full.from || ''}`,
    `To: ${full.to_address || full.to || ''}`,
    ...(cc ? [`Cc: ${cc}`] : []),
    `Subject: ${stampSubject(full)}`,
    `Date: ${formatReceived(full.received_at)}`,
    '',
    '---',
    '',
    emailNoteBodyText(full),
  ];
  const att = Array.isArray(full.attachments) ? full.attachments : [];
  if (att.length) {
    lines.push('', `Attachments (${att.length}): ${att.map(buildFirmFileObjectDescription).join(', ')}`);
  }
  return lines.join('\n').slice(0, NOTE_BODY_PLAIN_MAX);
}

export function escapeHtmlText(text) { // 18 Sep 2026 JCH: NC-1641 — exported for the notes-table expansion
  // 22 Sep 2026 JCH: NC-1641 — escape only. Email-in decodes stored &lt; in the iframe. Decoding here
  // made a memo's list disagree with the editor, and Convert would rewrite the saved note.
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function plainTextToNoteHtml(text) {
  const s = text != null ? String(text) : '';
  if (!s.trim()) return '';
  return `<div class="email-body-plain">${escapeHtmlText(s).replace(/(?:\r\n|\r|\n)/g, '<br>')}</div>`;
}

/** Marker written by buildNoteBodyHtmlHeader — true for note bodies imported from a connected email. */
export function noteBodyHasImportedEmail(noteBody) {
  return typeof noteBody === 'string' && noteBody.indexOf('email-note-header') !== -1;
}

/** 28 Aug 2026 JCH: NC-1644 — full HTML documents (connected emails, web pages) that CKEditor
 * cannot edit without mangling; Copy Note stores these as extracted plain text instead. */
export function noteBodyIsHtmlDocument(noteBody) {
  const s = typeof noteBody === 'string' ? noteBody : '';
  // 28 Aug 2026 JCH: NC-1644 — full documents only. A lone <style> in a CKEditor memo must
  // keep html (NC-1638); connected emails already have email-note-header or <html>/<head>.
  return noteBodyHasImportedEmail(s) || /<html[\s>]|<!DOCTYPE|<head[\s>]/i.test(s);
}

/** 28 Aug 2026 JCH: NC-1644 — readable text from a stored note body, incl. full email/web documents.
 * Order matters (NC-1641): non-visible blocks first (or their CSS/JS text leaks), tags before entity
 * decode (or &lt;addresses&gt; become <tags> and get eaten), break tag + following source newline
 * counts ONCE (or old <p>-per-line stamps double-space). */
export function htmlNoteToPlainText(raw) {
  const s = raw != null ? String(raw) : '';
  if (!s.trim()) return '';
  let text = removeNonVisibleHtmlBlocks(s)
    .replace(/<br\s*\/?>(?:[ \t]*\r?\n)?/gi, '\n')
    .replace(/<\/(?:p|div|tr)\s*>(?:[ \t]*\r?\n)?/gi, '\n')
    .replace(/<p[^>]*>(?:[ \t]*\r?\n)?/gi, '')
    .replace(/<hr\b[^>]*>(?:[ \t]*\r?\n)?/gi, '\n---\n')
    .replace(/<[^>]*>/g, '');
  text = decodeHtmlEntitiesRegex(text); // three passes, so &amp;lt; still becomes <
  return text
    .replace(/\u00a0/g, ' ')
    .replace(/\uFFFD/g, "'")
    .replace(/[ \t]+$/gm, '') // space-only lines must collapse like empty ones
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// 24 Aug 2026 JCH: NC-1641 — one <p> with <br> joins stays compact everywhere, incl. CKEditor (which
// strips the div/class); Cc when present; attachments above the rule. Font sizing lives in CSS.
function buildNoteBodyHtmlHeader(mailboxLabel, full) {
  const att = Array.isArray(full.attachments) ? full.attachments : [];
  const cc = full.cc != null ? String(full.cc).trim() : '';
  const lines = [
    `<strong>Mailbox:</strong> ${escapeHtmlText(mailboxLabel)}`,
    `<strong>From:</strong> ${escapeHtmlText(full.from || '')}`,
    `<strong>To:</strong> ${escapeHtmlText(full.to_address || full.to || '')}`,
  ];
  if (cc) lines.push(`<strong>Cc:</strong> ${escapeHtmlText(cc)}`);
  lines.push(`<strong>Subject:</strong> ${escapeHtmlText(stampSubject(full))}`);
  lines.push(`<strong>Date:</strong> ${escapeHtmlText(formatReceived(full.received_at))}`);
  if (att.length) {
    lines.push(
      `<strong>Attachments (${att.length}):</strong> ${escapeHtmlText(att.map(buildFirmFileObjectDescription).join(', '))}`,
    );
  }
  // join with <br> only — a source newline after <br> doubles into blank lines in plain-text conversions
  return `<div class="email-note-header">\n<p>${lines.join('<br>')}</p>\n<hr>\n</div>\n`;
}

function emailNoteBodyHtml(full, { mailboxId, messageId, apiBase } = {}) {
  let html = full.body_html != null ? String(full.body_html).trim() : '';
  if (html) {
    // Keep full document — buildNoteIframeSrcdoc lifts head CSS / body attrs like the connect preview.
    return prepareEmailBodyHtml(html, {
      mailboxId,
      messageId,
      attachments: full.attachments,
      apiBase,
    });
  }
  return plainTextToNoteHtml(emailNoteBodyText(full));
}

function hasSourceHtmlBody(full) {
  if (full && typeof full.has_html_body === 'boolean') return full.has_html_body;
  return !!(full && full.body_html != null && String(full.body_html).trim());
}

/** Connect note body — HTML emails get iframe (CKEditor cannot render email CSS); plain letters stay plain text. */
export function buildConnectNoteContent(mailboxLabel, full, { mailboxId, messageId, apiBase } = {}) {
  if (hasSourceHtmlBody(full)) {
    const note = (
      buildNoteBodyHtmlHeader(mailboxLabel, full)
      + emailNoteBodyHtml(full, { mailboxId, messageId, apiBase })
    ).slice(0, NOTE_BODY_HTML_MAX);
    return { note, html: true };
  }
  return { note: buildNoteBody(mailboxLabel, full), html: false };
}

/** 11 Aug 2026 JCH: convert descriptions default to filename, matching manual firm-file labels. */
function withDefaultAttachmentDescriptions(attachments) {
  return (Array.isArray(attachments) ? attachments : [])
    .filter((a) => a && a.filename)
    .map((a) => {
      const raw = a.file_description != null ? String(a.file_description).trim() : '';
      const fallback = String(a.filename || '');
      return {
        ...a,
        filename: a.filename,
        content_type: a.content_type,
        file_description: raw !== '' ? raw : fallback,
      };
    });
}

/** Stored firm file_object.file_description — plain label like manual upload (note type email in holds provenance). */
export function buildFirmFileObjectDescription(att) {
  const desc = att && att.file_description != null ? String(att.file_description).trim() : '';
  const fn = att && att.filename ? String(att.filename).trim() : '';
  return desc || fn || '';
}

/** Map Connect attachment rows → convert API attachments[] (always send array so labels apply). */
export function buildConvertAttachmentParams(attachments) {
  return withDefaultAttachmentDescriptions(attachments)
    .filter((a) => a && a.attachmentId)
    .map((a) => ({
      attachmentId: a.attachmentId,
      filename: a.filename,
      mimeType: a.content_type || a.mimeType || 'application/octet-stream',
      description: buildFirmFileObjectDescription(a),
    }));
}

/** Turn axios / fetch / Error values into a short user-facing string. */
export function formatConnectError(err) {
  if (!err) return 'Unknown error';
  if (typeof err === 'string') return err;
  const direct = err.message || err.userMessage;
  if (direct && String(direct).trim()) return String(direct).trim();
  const data = err.response && err.response.data;
  if (typeof data === 'string' && data.trim()) return data.trim();
  if (data && typeof data === 'object') {
    if (data.message) return String(data.message);
    if (Array.isArray(data.errors) && data.errors[0] && data.errors[0].message) {
      return String(data.errors[0].message);
    }
  }
  return 'Unknown error';
}

function connectHardFailDetail(row) {
  if (!row || !Array.isArray(row.errors) || !row.errors.length) return 'Connect failed.';
  return row.errors
    .map((e) => (typeof e === 'string' ? e : formatConnectError(e)))
    .filter(Boolean)
    .join('\n') || 'Connect failed.';
}

function connectHardFailUserMessageFromDetail(detail) {
  const d = String(detail || '');
  if (/could not be loaded/i.test(d)) return 'Email could not be loaded.';
  if (/convert api is not available/i.test(d)) return 'Email could not be connected.';
  return 'Connect may have partially completed. Check the firm before trying again.';
}

/**
 * Hard fail (no note) → ErrorDialog only (app pattern). Short user line + For Developers detail.
 */
export function connectHardFailDialogOptions(row) {
  const forDevelopers = connectHardFailDetail(row);
  return {
    errorMessage: connectHardFailUserMessageFromDetail(forDevelopers),
    forDevelopers,
  };
}

/** Bulk connect where every selected email failed before/without a note. */
export function connectBatchAllFailDialogOptions({ results, failCount } = {}) {
  const failed = (results || []).filter((r) => r && !r.noteCreated);
  const first = failed.find((r) => r.errors && r.errors.length) || failed[0];
  const base = connectHardFailDialogOptions(first);
  const n = Number.isFinite(failCount) && failCount > 0 ? failCount : failed.length;
  if (n > 1) {
    base.errorMessage = `Connect may have partially completed for ${n} emails. Check the firm before trying again.`;
    base.forDevelopers = failed
      .map((r) => {
        const id = r && r.messageId != null ? String(r.messageId) : '?';
        return `${id}: ${connectHardFailDetail(r)}`;
      })
      .join('\n\n');
  }
  return base;
}

/**
 * Popup outcome banner after a partial connect — options, not orders.
 * Snack stays short via connectRowSnackMessage.
 */
export const CONNECT_PARTIAL_BANNER_HINT =
  'The note was connected to the firm, but some files may not have attached. '
  + 'You can open the note to check, or move this email to trash so it isn\'t connected again.';

/** User-facing snack text from a single connect batch row (success / partial only). */
export function connectRowSnackMessage(row, fid) {
  if (!row || !row.noteCreated) {
    // Prefer ErrorDialog via connectHardFailDialogOptions — short fallback if snacked
    return connectHardFailUserMessageFromDetail(connectHardFailDetail(row));
  }
  const firm = fid != null ? String(fid) : 'firm';
  if (!row.errors.length) {
    return `Imported email as note on firm ${firm}.`;
  }
  // 11 Jul 2026 JCH: short snack only — popup banner owns next-step options
  if (row.attachmentExpected > 0 && row.attachmentLinked === 0) {
    return `Note created on firm ${firm} but files didn't attach.`;
  }
  if (row.attachmentExpected > row.attachmentLinked) {
    return `Note created on firm ${firm} but some files didn't attach.`;
  }
  // HTML body follow-up / mark-connected / other post-convert steps
  return `Note created on firm ${firm} but the note body wasn't fully updated.`;
}

/** Message ids that are not fully successful (partial or hard-fail) — keep selected on the list. */
export function connectBatchProblemMessageIds(results) {
  return (Array.isArray(results) ? results : [])
    .filter((r) => r && !isFullConnectSuccess(r))
    .map((r) => r.messageId)
    .filter((id) => id !== undefined && id !== null);
}

/**
 * Durable list banner after mixed bulk connect — scorecard + options.
 * Snack stays short via connectBatchSnackMessage.
 */
export function connectBatchMixedBannerHint({ results, failCount = 0 } = {}) {
  const partial = (results || []).filter((r) => r && r.noteCreated && r.errors && r.errors.length).length;
  const fails = Number(failCount) || 0;
  const problems = partial + fails;
  if (!problems) return '';

  const bits = [];
  if (partial) {
    bits.push(partial === 1 ? '1 may be missing files' : `${partial} may be missing files`);
  }
  if (fails) {
    bits.push(fails === 1 ? "1 didn't connect" : `${fails} didn't connect`);
  }
  const lead = problems === 1
    ? '1 was not fully successful'
    : `${problems} were not fully successful`;
  return `${lead} — ${bits.join('; ')}. `
    + 'You can open an email to check, or trash when you\'re done.';
}

/**
 * Summary for bulk connect (Email.vue). All-fail is handled with ErrorDialog, not this snack.
 * Mixed: short scorecard only (sticky banner + selection own the rest). Pass trashed when known.
 */
export function connectBatchSnackMessage({
  importedNotes, failCount, results, fid, trashed = 0,
} = {}) {
  const ok = (importedNotes || []).length;
  const rows = Array.isArray(results) ? results : [];
  const partial = rows.filter((r) => r && r.noteCreated && r.errors && r.errors.length).length;
  const full = rows.filter((r) => isFullConnectSuccess(r)).length;
  const fails = Number(failCount) || 0;
  const total = rows.length || (ok + fails);
  const firm = fid != null ? String(fid) : 'firm';

  if (fails && !ok) {
    return {
      color: 'error',
      hardFail: true,
      msg: connectBatchAllFailDialogOptions({ results, failCount: fails }).errorMessage,
    };
  }

  const hasProblems = partial > 0 || (fails > 0 && ok > 0);
  if (hasProblems) {
    // Single-row partial (rare on list bulk) keeps the popup-style short snack.
    if (ok === 1 && partial === 1 && !fails) {
      const firstPartial = rows.find((r) => r && r.noteCreated && r.errors && r.errors.length);
      return {
        color: 'warning',
        mixed: true,
        msg: firstPartial ? connectRowSnackMessage(firstPartial, firm) : `Of ${total} on firm ${firm}.`,
      };
    }
    let fullBit = '';
    if (full > 0) {
      fullBit = trashed > 0 && trashed >= full
        ? `${full} fully connected & trashed`
        : `${full} fully connected`;
    }
    return {
      color: 'warning',
      mixed: true,
      msg: fullBit
        ? `Of ${total} on firm ${firm}: ${fullBit}.`
        : `Of ${total} on firm ${firm}.`,
    };
  }

  return {
    msg: needsEmailApi('connectedBucket')
      ? `Imported ${ok} email(s) as notes on firm ${firm}.`
      : `Imported ${ok} email(s) as notes on firm ${firm}. View them under Connected.`,
  };
}

function attachmentsOverrideForMessage(attachmentsOverridesByMessageId, messageId) {
  if (!attachmentsOverridesByMessageId || messageId === undefined || messageId === null) return undefined;
  const keys = [messageId, String(messageId)];
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    if (Object.prototype.hasOwnProperty.call(attachmentsOverridesByMessageId, k)) {
      const raw = attachmentsOverridesByMessageId[k];
      return Array.isArray(raw) ? raw : undefined;
    }
  }
  return undefined;
}

function suppliedMessageDetail(messageDetailsById, messageId) {
  if (!messageDetailsById || messageId === undefined || messageId === null) return undefined;
  const keys = [messageId, String(messageId)];
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    if (Object.prototype.hasOwnProperty.call(messageDetailsById, k)) return messageDetailsById[k];
  }
  return undefined;
}

function stampSubject(full) {
  const subj = full && full.subject != null ? String(full.subject).trim() : '';
  return (subj || '(no subject)').slice(0, 500);
}

function reLineForNote(full, messageId, reByMessageId) {
  if (reByMessageId && messageId !== undefined && messageId !== null) {
    const keys = [messageId, String(messageId)];
    for (let i = 0; i < keys.length; i += 1) {
      const k = keys[i];
      if (Object.prototype.hasOwnProperty.call(reByMessageId, k)) {
        const raw = reByMessageId[k];
        if (raw != null && String(raw).trim() !== '') {
          return String(raw).trim().slice(0, 500);
        }
      }
    }
  }
  return stampSubject(full);
}

/**
 * 8 Aug 2026 JCH: Connect via convert API (server-side attachments).
 * Plain note bodies go in the convert request. HTML bodies use NotesAPIService afterward so
 * html:true is saved and large marketing emails keep the existing multipart-safe path.
 * @returns {{ importedIds: string[], importedNotes: object[], failCount: number, results: object[] }}
 */
export async function runEmailConnectBatch({
  client,
  router,
  username,
  mailboxLabel,
  mailboxId,
  messageRows,
  fid,
  status,
  staffUsername,
  attachmentsOverridesByMessageId,
  messageDetailsById,
  reByMessageId,
}) {
  const importedIds = [];
  const importedNotes = [];
  const results = [];
  let failCount = 0;

  for (const row of messageRows) {
    const rowMailboxId = (row && row.mailbox_id) || mailboxId;
    const result = {
      messageId: row.id,
      noteCreated: false,
      nid: null,
      attachmentExpected: 0,
      attachmentLinked: 0,
      errors: [],
    };

    try {
      if (!client || typeof client.convertToNote !== 'function') {
        throw new Error('Convert API is not available.');
      }

      const full = suppliedMessageDetail(messageDetailsById, row.id)
        || await client.getMessage(rowMailboxId, row.id);
      if (!full) {
        result.errors.push('Email could not be loaded.');
        failCount += 1;
        results.push(result);
        continue;
      }

      const staff = (staffUsername && String(staffUsername).trim()) || username;
      const overrideList = attachmentsOverrideForMessage(attachmentsOverridesByMessageId, row.id);
      const attachmentRows = overrideList !== undefined ? overrideList : full.attachments;
      const convertAttachments = buildConvertAttachmentParams(attachmentRows);
      const apiBase = process.env.VUE_APP_API_URL || '';
      const noteSource = { ...full, attachments: attachmentRows };
      const { note, html } = buildConnectNoteContent(mailboxLabel, noteSource, {
        mailboxId: rowMailboxId,
        messageId: row.id,
        apiBase,
      });
      const wantsBodyPlain = !!(full.body != null && String(full.body).trim());
      const wantsBodyHtml = hasSourceHtmlBody(full);
      // convert also attaches email.txt / email.html when those source parts are present
      result.attachmentExpected = convertAttachments.length
        + (wantsBodyPlain ? 1 : 0)
        + (wantsBodyHtml ? 1 : 0);

      const converted = await client.convertToNote(rowMailboxId, row.id, {
        fid,
        status,
        staff,
        re: reLineForNote(full, row.id, reByMessageId),
        // HTML follows below via NotesAPIService so html:true and large multipart bodies are handled.
        note: html ? '' : note,
        attachments: convertAttachments,
      });
      const nid = converted && converted.nid;
      if (!nid) {
        result.errors.push('Convert API returned no note id — check POST .../convert in Network.');
        failCount += 1;
        results.push(result);
        continue;
      }

      result.noteCreated = true;
      result.nid = nid;
      const files = Array.isArray(converted.files) ? converted.files : [];
      result.attachmentLinked = files.length;
      if (result.attachmentLinked < result.attachmentExpected) {
        result.errors.push(
          `Convert attached ${result.attachmentLinked} of ${result.attachmentExpected} expected files.`,
        );
      }

      // Convert deliberately leaves html choice to the caller; save formatted body for Edit Note iframe.
      if (html) {
        try {
          await notesAPIService.updateNote(nid, { note, html: true }, router);
        } catch (htmlErr) {
          result.errors.push(formatConnectError(htmlErr));
        }
      }

      try {
        await client.markMessagesConnected(rowMailboxId, [row.id]); // 1 Jul 2026 JCH: no-op on Gmail today; kept for future bucket
      } catch (markErr) {
        if (!result.errors.length) result.errors.push(formatConnectError(markErr));
      }

      importedIds.push(row.id);
      importedNotes.push({
        messageId: row.id,
        nid,
        fid,
        partial: result.errors.length > 0,
      });
    } catch (e) {
      if (result.noteCreated && result.nid) {
        result.errors.push(formatConnectError(e));
        importedIds.push(row.id);
        importedNotes.push({
          messageId: row.id,
          nid: result.nid,
          fid,
          partial: true,
        });
      } else {
        result.errors.push(formatConnectError(e));
        failCount += 1;
      }
    }

    results.push(result);
  }

  return { importedIds, importedNotes, failCount, results };
}
